// Series picker — sits between the tracks list and the race page.
// User picks one series + race mode + lap count + (in solo mode) the
// driver they're owning. The series brings its full team/driver
// lineup with it; ad-hoc grids are authored as ad-hoc series in the
// roster editor (Series mode). "Start race" stashes the bundle in
// sessionStorage and navigates to the race page, which resolves the
// series → teams → per-car { spec, livery, driverProfile, ... }
// entries before mounting the simulator.
//
// Persisted shape (sessionStorage[RACE_SETUP_KEY]):
//   {
//     trackId:      string,
//     seriesId:     string,
//     raceMode:     'god' | 'solo',
//     soloDriverId: string | null,
//     totalLaps:    number,
//   }
// Just IDs — race page does the catalog resolve so a team/driver/car
// edit on disk takes effect without rewriting the bridge payload.

import { listSeries, getSeries } from '../api-series.js';
import { listTeams } from '../api-teams.js';
import { listDrivers } from '../api-drivers.js';
import { listCars } from '../api-cars.js';
import { listTracks, getSelectedTrack } from '../api.js';
import { DEFAULT_RACE_LAPS } from '../../../simulator/src/race-state.js';

export const RACE_SETUP_KEY = 'race-manager:race-setup';
const MAX_GRID = 24;

export function setRaceSetup(setup) {
  try { sessionStorage.setItem(RACE_SETUP_KEY, JSON.stringify(setup)); } catch {}
}
export function getRaceSetup() {
  try {
    const v = sessionStorage.getItem(RACE_SETUP_KEY);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}
export function clearRaceSetup() {
  try { sessionStorage.removeItem(RACE_SETUP_KEY); } catch {}
}

export async function render(mountEl) {
  document.body.classList.add('home-route');
  const cleanup = () => document.body.classList.remove('home-route');

  // Selected track lives in sessionStorage (set by the tracks list /
  // play page) so the URL stays clean. Bare `#/race-setup` with no
  // selection lands on the empty-shell CTA below.
  const trackId = getSelectedTrack();
  if (!trackId) {
    mountEl.innerHTML = renderEmptyShell(`
      <h1 class="play-h1">No track <span class="accent">selected.</span></h1>
      <p class="play-subtitle">Pick a track from the catalog first.</p>
      <div class="setup-empty-cta"><a class="home-btn" href="#/tracks">Open tracks <span class="arrow">→</span></a></div>
    `);
    return cleanup;
  }

  mountEl.innerHTML = renderEmptyShell(`<p class="play-subtitle">Loading series…</p>`);

  let seriesList, teams, drivers, cars, tracks;
  try {
    [seriesList, teams, drivers, cars, tracks] = await Promise.all([
      listSeries(), listTeams(), listDrivers(), listCars(), listTracks(),
    ]);
  } catch (err) {
    mountEl.innerHTML = renderEmptyShell(`<p class="setup-error">Failed to load roster: ${escapeHtml(err.message)}</p>`);
    return cleanup;
  }

  const trackName = tracks.find(t => t.id === trackId)?.name ?? trackId;

  if (seriesList.length === 0) {
    mountEl.innerHTML = renderEmptyShell(`
      <h1 class="play-h1">Race Setup <span class="accent">·</span> ${escapeHtml(trackName)}</h1>
      <p class="play-subtitle">No series authored yet. Create one in the roster editor to pick a grid for this race.</p>
      <div class="setup-empty-cta">
        <a class="home-btn" href="#/roster?mode=series">Open roster <span class="arrow">→</span></a>
        <a class="home-btn secondary" href="#/tracks">← Tracks</a>
      </div>
    `);
    return cleanup;
  }

  const teamsById   = Object.fromEntries(teams.map(t => [t.id, t]));
  const driversById = Object.fromEntries(drivers.map(d => [d.id, d]));
  const carsById    = Object.fromEntries(cars.map(c => [c.id, c]));

  // Initial selection: prior pick if it matches this track AND the
  // referenced series still exists; otherwise default to the first
  // series in the list. First-time visits always land on series[0].
  const prior = getRaceSetup();
  const priorMatchesTrack = prior?.trackId === trackId;
  const priorSeriesValid  = priorMatchesTrack
    && typeof prior?.seriesId === 'string'
    && seriesList.some(s => s.id === prior.seriesId);
  const initial = {
    seriesId:     priorSeriesValid ? prior.seriesId : seriesList[0].id,
    // Solo is the default — racing IS the manager game's hero loop.
    // God mode is only sticky if the user explicitly picked it last
    // time on this track; everything else (no prior, prior was solo,
    // prior was for a different track) lands on solo.
    raceMode:     priorMatchesTrack && prior?.raceMode === 'god' ? 'god' : 'solo',
    soloDriverId: priorMatchesTrack ? (prior?.soloDriverId ?? null) : null,
    totalLaps:    priorMatchesTrack && Number.isFinite(prior?.totalLaps) && prior.totalLaps > 0
      ? Math.floor(prior.totalLaps)
      : DEFAULT_RACE_LAPS,
  };

  mountEl.innerHTML = renderShell(seriesList, initial, trackName);
  await bind(mountEl, trackId, teamsById, driversById, carsById, initial);
  return cleanup;
}

function renderEmptyShell(inner) {
  return `
    <div class="home-root setup-root">
      <nav class="home-nav">
        <span class="home-brand">Apex Slayer <span class="sub">// Race Engineer</span></span>
        <div class="home-nav-spacer"></div>
        <a href="#/play" class="home-nav-link">Pit Lane</a>
        <a href="#/tracks" class="home-nav-link">Tracks</a>
        <a href="#/roster" class="home-nav-link">Roster</a>
      </nav>
      <section class="play-hero setup-empty">
        <div class="home-section-label">// Grid up</div>
        ${inner}
      </section>
      <div class="play-spacer"></div>
    </div>
  `;
}

function renderShell(seriesList, initial, trackName) {
  const isSolo = initial.raceMode === 'solo';
  return `
    <div class="home-root setup-root">
      <nav class="home-nav">
        <span class="home-brand">Apex Slayer <span class="sub">// Race Engineer</span></span>
        <div class="home-nav-spacer"></div>
        <a href="#/play" class="home-nav-link">Pit Lane</a>
        <a href="#/tracks" class="home-nav-link">Tracks</a>
        <a href="#/roster" class="home-nav-link">Roster</a>
      </nav>

      <section class="play-hero">
        <div class="home-section-label">// Grid up</div>
        <h1 class="play-h1">Race Setup <span class="accent">·</span> ${escapeHtml(trackName)}</h1>
        <p class="play-subtitle">Pick a series, choose your seat, set the lap count. Lineup updates live as you change the series.</p>
      </section>

      <section class="setup-grid">
        <div class="setup-col setup-col-form">
          <div class="setup-card">
            <div class="setup-card-head">
              <div class="setup-card-num">/ 01</div>
              <div class="setup-card-title">Configuration</div>
            </div>
            <div class="setup-options">
              <div class="setup-option-row">
                <span class="setup-option-label">Series</span>
                <select id="setup-series" class="setup-input">
                  ${seriesList.map(s => {
                    const sub = [
                      s.shortName,
                      `${s.teamCount} team${s.teamCount === 1 ? '' : 's'}`,
                      s.classCount > 1 ? `${s.classCount} classes` : null,
                    ].filter(Boolean).join(' · ');
                    return `<option value="${escapeAttr(s.id)}" ${s.id === initial.seriesId ? 'selected' : ''}>
                      ${escapeHtml(s.name)}${sub ? ` — ${escapeHtml(sub)}` : ''}
                    </option>`;
                  }).join('')}
                </select>
              </div>
              <div class="setup-option-row">
                <span class="setup-option-label">Mode</span>
                <div class="setup-mode-group" role="group">
                  <button type="button" class="setup-mode-btn ${isSolo ? 'active' : ''}" data-mode="solo">Solo</button>
                  <button type="button" class="setup-mode-btn ${isSolo ? '' : 'active'}" data-mode="god">God mode</button>
                </div>
              </div>
              <div class="setup-option-row setup-option-solo" ${isSolo ? '' : 'hidden'}>
                <span class="setup-option-label">Driver</span>
                <select id="setup-driver" class="setup-input"></select>
              </div>
              <div class="setup-option-row setup-option-god" ${isSolo ? 'hidden' : ''}>
                <span class="setup-option-label">Role</span>
                <p class="setup-god-note">You'll control every car on the grid and make all strategy calls — pit stops, drive modes, the works.</p>
              </div>
              <div class="setup-option-row">
                <span class="setup-option-label">Laps</span>
                <input id="setup-laps" class="setup-input setup-input-laps" type="number" min="1" max="999" value="${initial.totalLaps}" />
              </div>
            </div>
          </div>

          <div class="setup-launch">
            <div class="setup-launch-summary" id="setup-summary">Loading lineup…</div>
            <button class="home-btn setup-launch-btn" id="setup-start" disabled>
              Start race <span class="arrow">→</span>
            </button>
            <a class="setup-launch-back" href="#/tracks">← Back to tracks</a>
          </div>
        </div>

        <div class="setup-col setup-col-detail">
          <div class="setup-card">
            <div class="setup-card-head">
              <div class="setup-card-num">/ 02</div>
              <div class="setup-card-title">Lineup</div>
            </div>
            <div class="setup-series-detail" id="setup-series-detail"></div>
          </div>
        </div>
      </section>

      <div class="play-spacer"></div>
    </div>
  `;
}

async function bind(mountEl, trackId, teamsById, driversById, carsById, initial) {
  const startBtn   = mountEl.querySelector('#setup-start');
  const summaryEl  = mountEl.querySelector('#setup-summary');
  const detailEl   = mountEl.querySelector('#setup-series-detail');
  const seriesSel  = mountEl.querySelector('#setup-series');
  const modeBtns   = mountEl.querySelectorAll('.setup-mode-btn');
  const soloRow    = mountEl.querySelector('.setup-option-solo');
  const godRow     = mountEl.querySelector('.setup-option-god');
  const driverSel  = mountEl.querySelector('#setup-driver');
  const lapsInput  = mountEl.querySelector('#setup-laps');

  let seriesId     = initial.seriesId;
  let raceMode     = initial.raceMode;
  let soloDriverId = initial.soloDriverId;
  // Cache resolved series objects so flipping back to a previously-
  // viewed series doesn't refetch — files are tiny but the round-trip
  // would flicker the lineup detail otherwise.
  const seriesCache = new Map();

  const fetchSeries = async (id) => {
    if (seriesCache.has(id)) return seriesCache.get(id);
    const s = await getSeries(id);
    seriesCache.set(id, s);
    return s;
  };

  // Drivers visible in the solo picker = every driver referenced by
  // any team in any class of the active series. Preserves the prior
  // pick if still in the list; otherwise falls through to first option.
  const refreshDriverOptions = (series) => {
    const opts = [];
    for (const cls of series.classes) {
      for (const tid of cls.teamIds) {
        const team = teamsById[tid];
        if (!team) continue;
        for (const e of (team.entries ?? [])) {
          if (!e.driverId) continue;
          const d = driversById[e.driverId];
          if (!d) continue;
          const num = e.carNumber != null ? `#${e.carNumber} ` : '';
          const teamSuffix  = team.shortName ? ` · ${team.shortName}` : '';
          const classSuffix = (series.classes.length > 1 && cls.name) ? ` · ${cls.name}` : '';
          opts.push(`<option value="${escapeAttr(e.driverId)}">${num}${escapeHtml(d.name)}${teamSuffix}${classSuffix}</option>`);
        }
      }
    }
    driverSel.innerHTML = opts.length > 0
      ? opts.join('')
      : '<option value="" disabled>No drivers in this series</option>';
    if (soloDriverId && Array.from(driverSel.options).some(o => o.value === soloDriverId)) {
      driverSel.value = soloDriverId;
    } else {
      soloDriverId = driverSel.value || null;
    }
  };

  const renderDetail = (series) => {
    let totalCars = 0;
    detailEl.innerHTML = series.classes.map(cls => {
      const teamRows = cls.teamIds.map(tid => {
        const t = teamsById[tid];
        if (!t) {
          return `<div class="setup-team-row missing"><span class="error small">Missing team: ${escapeHtml(tid)}</span></div>`;
        }
        const car = carsById[t.carId];
        const carLabel = car ? escapeHtml(car.name) : `<span class="muted small">no car</span>`;
        const driverChips = (t.entries ?? []).map(e => {
          const d = driversById[e.driverId];
          const num = e.carNumber != null ? `#${e.carNumber}` : '';
          if (!d) return `<span class="setup-driver missing">${escapeHtml(e.driverId)} ${num} (missing)</span>`;
          return `<span class="setup-driver">${num} ${escapeHtml(d.name)}${d.shortName ? ` (${escapeHtml(d.shortName)})` : ''}</span>`;
        }).join('');
        const driverCount = (t.entries ?? []).filter(e => e.driverId && driversById[e.driverId]).length;
        totalCars += driverCount;
        const swatch = `<span class="livery-swatch" style="background:${escapeAttr(t.primary)};border-color:${escapeAttr(t.accent)}"></span>`;
        return `
          <div class="setup-team-row">
            <div class="setup-team-body">
              <div class="setup-team-head">
                ${swatch}
                <strong>${escapeHtml(t.name)}</strong>
                <span class="muted small">${escapeHtml(t.shortName || '')}</span>
                <span class="muted small" style="margin-left:auto">${carLabel}</span>
              </div>
              <div class="setup-driver-row">${driverChips || '<span class="muted small">no drivers</span>'}</div>
            </div>
          </div>
        `;
      }).join('');
      const head = series.classes.length > 1
        ? `<h3 class="setup-class-head">${escapeHtml(cls.name || 'Unnamed class')} <span class="muted small">${cls.teamIds.length} team${cls.teamIds.length === 1 ? '' : 's'}</span></h3>`
        : '';
      return `<div class="setup-class">${head}<div class="setup-team-list">${teamRows || '<p class="muted small">No teams in this class.</p>'}</div></div>`;
    }).join('');
    return totalCars;
  };

  const updateGate = (totalCars) => {
    const overCap = totalCars > MAX_GRID;
    summaryEl.innerHTML = totalCars === 0
      ? '<span class="muted small">This series has no drivers — pick another, or add teams in the roster editor.</span>'
      : `<strong>${totalCars}</strong> car${totalCars === 1 ? '' : 's'} on the grid` +
        (overCap ? ` <span class="error small">— exceeds grid cap of ${MAX_GRID}</span>` : '');
    const soloMissingDriver = raceMode === 'solo' && (!soloDriverId || !driverSel.value);
    startBtn.disabled = totalCars === 0 || overCap || soloMissingDriver;
  };

  const onSeriesChange = async () => {
    detailEl.innerHTML = '<p class="muted">Loading…</p>';
    let series;
    try {
      series = await fetchSeries(seriesId);
    } catch (err) {
      detailEl.innerHTML = `<p class="error">Failed to load series: ${escapeHtml(err.message)}</p>`;
      summaryEl.textContent = '';
      startBtn.disabled = true;
      return;
    }
    const totalCars = renderDetail(series);
    refreshDriverOptions(series);
    updateGate(totalCars);
  };

  seriesSel.addEventListener('change', () => {
    seriesId = seriesSel.value;
    onSeriesChange();
  });
  modeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      raceMode = btn.dataset.mode;
      modeBtns.forEach(b => b.classList.toggle('active', b === btn));
      soloRow.hidden = raceMode !== 'solo';
      godRow.hidden  = raceMode === 'solo';
      const series = seriesCache.get(seriesId);
      if (series) updateGate(countCars(series, teamsById, driversById));
    });
  });
  driverSel.addEventListener('change', () => {
    soloDriverId = driverSel.value || null;
    const series = seriesCache.get(seriesId);
    if (series) updateGate(countCars(series, teamsById, driversById));
  });

  startBtn.addEventListener('click', () => {
    const totalLaps = Math.max(1, Math.floor(Number(lapsInput.value) || DEFAULT_RACE_LAPS));
    setRaceSetup({
      trackId,
      seriesId,
      raceMode,
      soloDriverId: raceMode === 'solo' ? soloDriverId : null,
      totalLaps,
    });
    // Track is already in sessionStorage (read at mount); no need to
    // push it onto the URL. Race page reads it the same way.
    window.location.hash = '#/race';
  });

  await onSeriesChange();
}

// Same head-count math as renderDetail's totalCars accumulator,
// extracted so the mode/driver-change handlers can reuse the cached
// series without re-rendering the whole detail block.
function countCars(series, teamsById, driversById) {
  let total = 0;
  for (const cls of series.classes) {
    for (const tid of cls.teamIds) {
      const t = teamsById[tid];
      if (!t) continue;
      for (const e of (t.entries ?? [])) {
        if (e.driverId && driversById[e.driverId]) total++;
      }
    }
  }
  return total;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
